import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DocumentForm from './DocumentForm';
import DocumentList from './DocumentList';
import DocumentView from './DocumentView';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DocumentsModule = ({ onOpenPaymentManager, onEditDocument, onStatusChange, onDeleteDocument }) => {
  const [currentView, setCurrentView] = useState('list');
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  
  // Batch selection states
  const [selectedDocuments, setSelectedDocuments] = useState([]);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/documents`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const handleDocumentCreated = (newDocument) => {
    setDocuments([...documents, newDocument]);
    setCurrentView('list');
  };

  const handleDocumentSelect = (document) => {
    setSelectedDocument(document);
    setCurrentView('view');
  };

  // Batch selection functions
  const handleSelectDocument = (document) => {
    setSelectedDocuments(prev => {
      if (prev.find(doc => doc.id === document.id)) {
        return prev.filter(doc => doc.id !== document.id);
      } else {
        return [...prev, document];
      }
    });
  };

  const handleSelectAllDocuments = (checked) => {
    if (checked) {
      setSelectedDocuments(documents);
    } else {
      setSelectedDocuments([]);
    }
  };

  const handleBatchDelete = async () => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedDocuments.length} document(s) ? Cette action est irréversible.`)) {
      try {
        const deletePromises = selectedDocuments.map(document => 
          fetch(`${API}/documents/${document.id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          })
        );
        
        await Promise.all(deletePromises);
        alert(`${selectedDocuments.length} document(s) supprimé(s) avec succès !`);
        setSelectedDocuments([]);
        fetchDocuments(); // Refresh the list
        
        // Notify parent component
        if (onDeleteDocument) {
          selectedDocuments.forEach(doc => onDeleteDocument(doc.id));
        }
      } catch (error) {
        console.error('Error deleting documents:', error);
        alert('Erreur lors de la suppression des documents');
      }
    }
  };

  const handleBatchArchive = async () => {
    if (window.confirm(`Êtes-vous sûr de vouloir archiver ${selectedDocuments.length} document(s) ?`)) {
      try {
        const archivePromises = selectedDocuments.map(document => 
          fetch(`${API}/documents/${document.id}/status`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ status: 'archivé' })
          })
        );
        
        await Promise.all(archivePromises);
        alert(`${selectedDocuments.length} document(s) archivé(s) avec succès !`);
        setSelectedDocuments([]);
        fetchDocuments(); // Refresh the list
      } catch (error) {
        console.error('Error archiving documents:', error);
        alert('Erreur lors de l\'archivage des documents');
      }
    }
  };

  const handleBatchDuplicate = async () => {
    if (window.confirm(`Êtes-vous sûr de vouloir dupliquer ${selectedDocuments.length} document(s) ?`)) {
      try {
        const duplicatePromises = selectedDocuments.map(async (document) => {
          // Create a new reference number for each document
          const newReference = `${document.type.toUpperCase()}-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`;
          
          // Prepare duplicate data
          const duplicatedData = {
            type: document.type,
            numero_reference: newReference,
            societe_id: document.societe_id,
            client: document.client,
            items: document.items || [],
            notes: document.notes || '',
            conditions: document.conditions || '',
            status: 'en_cours', // Reset status to in progress
            statut: 'en_cours',
            date_creation: new Date().toISOString(),
            template_style: document.template_style || 'standard'
          };
          
          return fetch(`${API}/documents`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(duplicatedData)
          });
        });
        
        await Promise.all(duplicatePromises);
        alert(`${selectedDocuments.length} document(s) dupliqué(s) avec succès !`);
        setSelectedDocuments([]);
        fetchDocuments(); // Refresh the list
      } catch (error) {
        console.error('Error duplicating documents:', error);
        alert('Erreur lors de la duplication des documents');
      }
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-green-600">Documents</h2>
          <p className="text-gray-600 mt-1">Gérez vos factures, devis et bons de commande</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setCurrentView('list')}
            className={`px-4 py-2 rounded-lg ${
              currentView === 'list'
                ? 'bg-baobab-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            📋 Liste
          </button>
        </div>
      </div>

      {/* Batch Actions Bar */}
      {selectedDocuments.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-700">
                {selectedDocuments.length} document(s) sélectionné(s)
              </span>
              <button
                onClick={() => setSelectedDocuments([])}
                className="text-sm text-green-600 hover:text-green-800"
              >
                Désélectionner tout
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleBatchDuplicate}
                className="px-3 py-1 text-sm bg-white text-blue-600 border border-blue-300 rounded hover:bg-blue-50 flex items-center gap-1"
                title="Dupliquer les documents sélectionnés"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Dupliquer
              </button>
              <button
                onClick={handleBatchArchive}
                className="px-3 py-1 text-sm bg-white text-yellow-600 border border-yellow-300 rounded hover:bg-yellow-50 flex items-center gap-1"
                title="Archiver les documents sélectionnés"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8l4 4m0 0l4-4m-4 4V3m8 10v8a2 2 0 01-2 2H6a2 2 0 01-2-2v-8" />
                </svg>
                Archiver
              </button>
              <button
                onClick={handleBatchDelete}
                className="px-3 py-1 text-sm bg-white text-red-600 border border-red-300 rounded hover:bg-red-50 flex items-center gap-1"
                title="Supprimer les documents sélectionnés"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {currentView === 'create' && (
        <DocumentForm onDocumentCreated={handleDocumentCreated} />
      )}
      {currentView === 'list' && (
        <DocumentList
          documents={documents}
          onDocumentSelect={handleDocumentSelect}
          onOpenPaymentManager={onOpenPaymentManager}
          onEditDocument={onEditDocument}
          onStatusChange={onStatusChange}
          onDeleteDocument={onDeleteDocument}
          selectedDocuments={selectedDocuments}
          onSelectDocument={handleSelectDocument}
          onSelectAllDocuments={handleSelectAllDocuments}
          onClearSelection={() => setSelectedDocuments([])}
          onRefresh={fetchDocuments}
        />
      )}
      {currentView === 'view' && selectedDocument && (
        <DocumentView
          document={selectedDocument}
          onBack={() => setCurrentView('list')}
          onEditDocument={onEditDocument}
        />
      )}
    </div>
  );
};

export default DocumentsModule;